import React, { useMemo, useState } from "react";
import { iconForDeliverable } from "../create-project-chat/create-project-chat-page";
import { ArrowLeftIcon, CheckIcon, Edit2Icon, TriangleAlertIcon } from "lucide-react";
import { Deliverable } from "tekkr-common/dist/model/project/project";
import { TaskList } from "./components/task-list";
import { useProject } from "../../data/project";
import { useProjectTasksQuery } from "../../data/queries/project-tasks";
import { Button } from "../../components/ui/button";
import Spinner from "../../components/ui/spinner";
import { ProjectWorkstreamIcon } from "../../components/shared/project/workstream-header";
import { useUpdateDeliverableMutation } from "../../data/mutations/update-deliverable";
import { useUpdateDeliverableTimelineMutation } from "../../data/mutations/update-deliverable-timelines";
import Contenteditable from "../../components/shared/contenteditable";
import { cn } from "../../lib/utils";
import { PlannedTimeline } from "../../components/shared/project/planned-timeline";
import { LinkedDocumentField } from "./components/linked-document-field";
import { OwnerSelector } from "../../components/shared/project/owner-selector";
import { StatusCircle } from "../../components/shared/project/status-circle";
import { computeStatusCounts, StatusCounts } from "../../data/project-progress";
import { formatDeliverableId } from "../../lib/projects/utils";

type Params = {
    workstreamId: string;
    deliverableId: string;
    onUnselectDeliverable?: () => void;
};

export function ProjectDeliverableSubpage({ workstreamId, deliverableId, ...props }: Params) {
    const { project } = useProject();
    const tasksQuery = useProjectTasksQuery();
    const tasks = useMemo(() => {
        if (!tasksQuery.data) {
            return null;
        }
        return tasksQuery.data.data.filter((t) => t.workstreamId === workstreamId && t.deliverableId === deliverableId);
    }, [tasksQuery.data, workstreamId, deliverableId]);

    const { getWorkstream, getDeliverable } = useProject();
    const ws = getWorkstream(workstreamId);
    const deliverable = getDeliverable(workstreamId, deliverableId);
    const wsIndex = project.outline.workstreams.findIndex((w) => w.id === workstreamId);
    const deliverableIndex = ws?.deliverables.findIndex((d) => d.id === deliverableId) ?? -1;

    const statusCounts = useMemo(() => (tasks ? computeStatusCounts(tasks) : undefined), [tasks]);

    if (!tasks) {
        return <div>loading...</div>;
    }

    return (
        <div className={"flex flex-col gap-6"}>
            <div className={"flex flex-row items-center"}>
                <div
                    className={
                        "group flex cursor-pointer flex-row items-center gap-1.5 text-muted-foreground transition-all hover:-translate-x-1 hover:text-foreground"
                    }
                    onClick={() => props.onUnselectDeliverable?.()}
                >
                    <ArrowLeftIcon className={"h-4 w-4 transition-all group-hover:translate-x-0.5"} />
                    <ProjectWorkstreamIcon ws={ws} index={wsIndex >= 0 ? wsIndex : undefined} size={"xs"} />
                    <div className={"text-sm font-medium"}>{ws.name}</div>
                </div>
            </div>
            <DeliverableHeader
                key={project.id + "-" + workstreamId + "-" + deliverableId}
                deliverable={deliverable!}
                workstreamId={workstreamId}
                workstreamIndex={wsIndex >= 0 ? wsIndex : undefined}
                deliverableIndex={deliverableIndex >= 0 ? deliverableIndex : undefined}
                statusCounts={statusCounts}
            />
            <DeliverableOwner deliverable={deliverable!} workstreamId={workstreamId} />

            <TaskList tasks={tasks} />
        </div>
    );
}

function DeliverableHeader({
    deliverable,
    statusCounts,
    ...props
}: {
    deliverable: Deliverable;
    workstreamId: string;
    workstreamIndex?: number;
    deliverableIndex?: number;
    statusCounts?: StatusCounts;
}) {
    const refLabel = formatDeliverableId(props.workstreamIndex, props.deliverableIndex);
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(deliverable.name);
    const [description, setDescription] = useState(deliverable.description);
    const DeliverableIcon = deliverable ? iconForDeliverable(deliverable) : undefined;

    const updateMutation = useUpdateDeliverableMutation(props.workstreamId, deliverable.id);

    const updateTimelineMutation = useUpdateDeliverableTimelineMutation(props.workstreamId, deliverable.id);

    function save() {
        updateMutation.mutate({ name, description });
        setIsEditing(false);
    }

    function cancel() {
        setIsEditing(false);
        setName(deliverable.name);
        setDescription(deliverable.description);
    }

    const [startDate, _setStartDate] = useState(deliverable.startDate);
    const [targetDate, _setTargetDate] = useState(deliverable.targetDate);

    function triggerTimelineUpdateIfPossible(startDate: string | undefined, targetDate: string | undefined) {
        if (startDate && targetDate) {
            updateTimelineMutation.mutate({ startDate, endDate: targetDate });
        }
    }
    function setStartDate(date: string | undefined) {
        _setStartDate(date);
        triggerTimelineUpdateIfPossible(date, targetDate);
    }
    function setTargetDate(date: string | undefined) {
        _setTargetDate(date);
        triggerTimelineUpdateIfPossible(startDate, date);
    }

    return (
        <div className={"flex flex-row items-start gap-4"}>
            <div className={"flex flex-1 flex-col gap-1.5"}>
                <h3 className={"flex flex-row items-center gap-2"}>
                    {DeliverableIcon && <DeliverableIcon className={"h-6 w-6 shrink-0"} />}
                    <Contenteditable
                        value={name}
                        onChange={setName}
                        disabled={!isEditing}
                        className={cn("transition-all", isEditing && "-mx-1 -my-0.5 rounded-md bg-input px-2 py-0.5")}
                    />
                </h3>

                <div className={"flex flex-row items-center gap-4"}>
                    <PlannedTimeline
                        from={startDate}
                        onFromChange={setStartDate}
                        to={targetDate}
                        onToChange={setTargetDate}
                        editable={true}
                    />
                    {!startDate !== !targetDate && (
                        <div
                            className={
                                "flex flex-row items-center rounded-md bg-destructive/10 px-1 py-0.5 text-sm font-medium text-destructive animate-in zoom-in-75"
                            }
                        >
                            <TriangleAlertIcon className={"me-1.5 h-4 w-4"} />
                            select both dates to save.
                        </div>
                    )}
                </div>

                <p
                    className={cn(
                        "text-sm text-muted-foreground transition-all",
                        isEditing && "rounded-md bg-input px-2 py-1 text-foreground"
                    )}
                >
                    <Contenteditable disabled={!isEditing} value={description} onChange={setDescription} />
                </p>

                {!isEditing && !updateMutation.isPending && (
                    <Button
                        onClick={() => setIsEditing(!isEditing)}
                        variant={"ghost"}
                        className={"-mt-1 self-start text-muted-foreground"}
                        size={"sm"}
                    >
                        <Edit2Icon className={"me-2 h-4 w-4"} /> Edit
                    </Button>
                )}

                {updateMutation.isPending && (
                    <div
                        className={
                            "mb-1 flex flex-row items-center gap-2 p-1 text-sm font-medium text-muted-foreground"
                        }
                    >
                        <Spinner className={"h-5 w-5"} /> Updating...
                    </div>
                )}

                {isEditing && (
                    <div className={"mt-1 flex flex-row items-center gap-2"}>
                        <Button onClick={save} size={"sm"}>
                            <CheckIcon className={"me-2 h-4 w-4"} /> Done
                        </Button>
                        <Button onClick={cancel} size={"sm"} variant={"ghost"}>
                            Cancel
                        </Button>
                    </div>
                )}

                <LinkedDocumentField
                    target={["deliverable-file", props.workstreamId, deliverable.id]}
                    label="Deliverable File:"
                    dialogTitle="Add Deliverable File"
                />
            </div>

            {statusCounts && statusCounts.total > 0 && (
                <StatusCircle counts={statusCounts} size="lg">
                    {refLabel !== "?" && (
                        <span className="text-xs font-semibold text-muted-foreground">{refLabel}</span>
                    )}
                </StatusCircle>
            )}
        </div>
    );
}

function DeliverableOwner(props: { deliverable: Deliverable; workstreamId: string }) {
    const [ownerId, setOwnerId] = useState(props.deliverable.ownerId ?? null);
    const updateMutation = useUpdateDeliverableMutation(props.workstreamId, props.deliverable.id);

    function handleOwnerChange(newOwnerId: string | null) {
        updateMutation.mutate({ ownerId: newOwnerId });
        setOwnerId(newOwnerId);
    }

    return <OwnerSelector ownerId={ownerId} onOwnerChange={handleOwnerChange} label={"Deliverable owner:"} />;
}
